/**
 * Risk Threshold Service – QMS Phase 2
 * CRUD for risk_thresholds; log violations in risk_threshold_violations.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const THRESHOLDS_COLLECTION = 'risk_thresholds';
const VIOLATIONS_COLLECTION = 'risk_threshold_violations';

export class RiskThresholdService {
  constructor() {
    this.thresholdsRef = collection(db, THRESHOLDS_COLLECTION);
    this.violationsRef = collection(db, VIOLATIONS_COLLECTION);
  }

  async createThreshold(data, userId) {
    const now = Timestamp.now();
    const payload = {
      riskId: data.riskId,
      metric: data.metric,
      operator: data.operator,
      value: data.value,
      triggerWorkflowId: data.triggerWorkflowId || null,
      isActive: data.isActive !== false,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    const ref = await addDoc(this.thresholdsRef, payload);
    return ref.id;
  }

  async getThreshold(thresholdId) {
    const ref = doc(this.thresholdsRef, thresholdId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async getThresholdsByRiskId(riskId) {
    if (!riskId) return [];
    const q = query(
      this.thresholdsRef,
      where('riskId', '==', riskId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async updateThreshold(thresholdId, updates, userId) {
    const ref = doc(this.thresholdsRef, thresholdId);
    await updateDoc(ref, {
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async deleteThreshold(thresholdId) {
    const ref = doc(this.thresholdsRef, thresholdId);
    await deleteDoc(ref);
  }

  /** Log a threshold violation (Phase 2: log only; no workflow trigger) */
  async logViolation(data, userId) {
    const now = Timestamp.now();
    const payload = {
      riskId: data.riskId,
      thresholdId: data.thresholdId,
      at: now,
      metricValue: data.metricValue,
      actionTaken: data.actionTaken || 'logged'
    };
    const ref = await addDoc(this.violationsRef, payload);
    return ref.id;
  }

  async getViolationsByRiskId(riskId, limit = 50) {
    if (!riskId) return [];
    const q = query(
      this.violationsRef,
      where('riskId', '==', riskId),
      orderBy('at', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limit).map(d => ({ id: d.id, ...d.data() }));
  }
}

export const riskThresholdService = new RiskThresholdService();
